// Persistent WebSocket connection for friend challenges and online presence.
// Separate from the matchmaking WebSocket used by WebSocketTransport.
// Stored on window.__wsClient for access by FriendsPanel.
import { ENV } from '../config/env.js'

type MessageHandler = (msg: Record<string, unknown>) => void

const RECONNECT_DELAY_MS = 3000
const MAX_RECONNECT_DELAY_MS = 30_000
const PING_INTERVAL_MS = 25_000

class FriendsSocketService {
  private ws: WebSocket | null = null
  private handlers: MessageHandler[] = []
  private reconnectDelay = RECONNECT_DELAY_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private stopped = false
  private accessToken: string | null = null
  private playerName: string | null = null
  private mmr = 0

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }

  init(accessToken: string, playerName: string, mmr: number): void {
    this.accessToken = accessToken
    this.playerName = playerName
    this.mmr = mmr
    this.stopped = false
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.identify()
      return
    }
    this.connect()
  }

  shutdown(): void {
    this.stopped = true
    this.clearTimers()
    this.ws?.close()
    this.ws = null
  }

  send(payload: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload))
    }
  }

  addHandler(fn: MessageHandler): void {
    this.handlers.push(fn)
  }

  removeHandler(fn: MessageHandler): void {
    this.handlers = this.handlers.filter((h) => h !== fn)
  }

  private connect(): void {
    if (this.stopped) return
    try {
      const ws = new WebSocket(ENV.WS_SERVER_URL)
      this.ws = ws

      ws.addEventListener('open', () => {
        this.reconnectDelay = RECONNECT_DELAY_MS
        this.identify()
        this.startPing()
      })

      ws.addEventListener('message', (e) => {
        try {
          const msg = JSON.parse(e.data as string) as Record<string, unknown>
          this.handlers.forEach((h) => h(msg))
        } catch { /* ignore parse errors */ }
      })

      ws.addEventListener('close', () => {
        this.clearTimers()
        if (!this.stopped) this.scheduleReconnect()
      })

      ws.addEventListener('error', () => {
        // close event follows automatically — reconnect handled there
      })
    } catch {
      if (!this.stopped) this.scheduleReconnect()
    }
  }

  private identify(): void {
    if (!this.accessToken) return
    this.send({
      type: 'AUTH_IDENTIFY',
      accessToken: this.accessToken,
      playerName: this.playerName ?? 'Player',
      mmr: this.mmr,
    })
  }

  private startPing(): void {
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'PING' }))
      }
    }, PING_INTERVAL_MS)
  }

  private scheduleReconnect(): void {
    this.reconnectTimer = setTimeout(() => {
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, MAX_RECONNECT_DELAY_MS)
      this.connect()
    }, this.reconnectDelay)
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
    if (this.pingTimer !== null) { clearInterval(this.pingTimer); this.pingTimer = null }
  }
}

export const friendsSocket = new FriendsSocketService()

// Expose on window so FriendsPanel can access it without importing the module
// (avoids a circular dependency through the barrel export chain).
;(window as Window & { __wsClient?: FriendsSocketService }).__wsClient = friendsSocket
